import { useCallback, useEffect, useMemo } from 'react';
import { v1 as uuid } from 'uuid';

import {
  Box,
  Button,
  ButtonVariants,
  CustomerLedgerUpdateTypeEnum,
  Divider,
  FormControlSizes,
  FormGroup,
  HStack,
  InlineFormattedNumber,
  Input,
  NumberInput,
  SearchSelect,
  Text,
  ToggleHorizontal,
  TransactionAutoProposalModeEnum,
  VStack,
  type ToggleHorizontalOption,
} from '@talos/kyoko';

import { AssetAliasSelector } from '@src/containers/Dealer/CustomerBalances/AssetAliasSelector';
import { useCustomerBalanceData, useTransferOptions } from '@src/containers/Dealer/CustomerBalances/hooks';
import type { CustomerBalanceTransactionDrawerState } from '@src/containers/Dealer/CustomerBalances/types';
import { useOnlineTransferMarkets } from '@src/containers/Transfers';
import { useFeatureFlag } from '@src/hooks';
import { noop } from 'lodash-es';

interface SectionHeaderProps {
  title: string;
  badge?: string;
}

function SectionHeader({ title, badge }: SectionHeaderProps) {
  return (
    <>
      <HStack justifyContent="flex-start" mb="spacing16" gap="spacing8">
        <Text color="colorTextImportant" fontWeight="fontWeightSemiBold" fontSize="fontSizeMedium">
          {title}
        </Text>
        {badge && (
          <Box
            px="spacing4"
            py="0"
            background="colors.gray.040"
            borderRadius="borderRadiusSmall"
            justifyContent="center"
            alignItems="center"
          >
            <Text color="colorTextSubtle" fontSize="fontSizeSmall">
              {badge}
            </Text>
          </Box>
        )}
      </HStack>
      <Divider mt="spacing16" mb="spacing16" mx="-spacing16" color="colors.gray.000" />
    </>
  );
}

const TRANSFER_METHOD_OPTIONS: ToggleHorizontalOption<'initiate' | 'manual'>[] = [
  { value: 'initiate', displayName: 'Initiate Transfer' },
  { value: 'manual', displayName: 'Book Manual Transfer' },
];

interface TransferSourceDestinationFormProps {
  drawerState: CustomerBalanceTransactionDrawerState;
  updateDrawerState: (updates: Partial<CustomerBalanceTransactionDrawerState>) => void;
  showOnlyConfirmTransferFlow?: boolean;
}

export function TransferSourceDestinationForm({
  drawerState,
  updateDrawerState,
  showOnlyConfirmTransferFlow = false,
}: TransferSourceDestinationFormProps) {
  const { customerTransactionAutoProposalMode } = useFeatureFlag();
  const providerOptions = useOnlineTransferMarkets();
  const { sourceOptions, destinationOptions } = useTransferOptions({ drawerState });

  const { sourceAccountBalance } = useCustomerBalanceData({ drawerState });

  // Create filtered transfer method options based on auto proposal mode
  const transferMethodOptions = useMemo(() => {
    if (
      // Only show initiate transfer when auto proposal mode is None and transfer type is withdraw
      customerTransactionAutoProposalMode === TransactionAutoProposalModeEnum.None &&
      drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw
    ) {
      return TRANSFER_METHOD_OPTIONS.filter(option => option.value === 'initiate');
    }
    if (
      // Only show manual transfer when auto proposal mode is None or transfer type is deposit
      customerTransactionAutoProposalMode === TransactionAutoProposalModeEnum.None ||
      drawerState.transferType === CustomerLedgerUpdateTypeEnum.Deposit
    ) {
      return TRANSFER_METHOD_OPTIONS.filter(option => option.value === 'manual');
    }
    return TRANSFER_METHOD_OPTIONS;
  }, [customerTransactionAutoProposalMode, drawerState.transferType]);

  // Update transfer method to 'manual' when only manual option is available
  useEffect(() => {
    const onlyManualAvailable = transferMethodOptions.length === 1 && transferMethodOptions[0].value === 'manual';
    if (onlyManualAvailable && drawerState.transferMethod !== 'manual') {
      updateDrawerState({ transferMethod: 'manual' });
    }
  }, [transferMethodOptions, drawerState.transferMethod, updateDrawerState]);

  const handleGenerateExternalID = useCallback(() => {
    updateDrawerState({ externalID: uuid() });
  }, [updateDrawerState]);

  const getBadgeText = (transferType?: CustomerLedgerUpdateTypeEnum) => {
    switch (transferType) {
      case CustomerLedgerUpdateTypeEnum.Deposit:
        return 'Deposit';
      case CustomerLedgerUpdateTypeEnum.Withdraw:
        return 'Withdraw';
      default:
        return transferType;
    }
  };

  return (
    <VStack gap="spacing16" w="100%">
      <Box background="colors.gray.030" p="spacing16" borderRadius="borderRadiusMedium" w="100%">
        <SectionHeader
          title={drawerState.transferMethod === 'initiate' ? 'Transfer' : 'Book Manual Transfer'}
          badge={getBadgeText(drawerState.transferType)}
        />

        {/* Transfer Method Toggle, only shown if multiple options are available */}
        {transferMethodOptions.length > 1 && (
          <FormGroup mb="spacing16" label="Transfer Method">
            <ToggleHorizontal
              value={drawerState.transferMethod}
              options={transferMethodOptions}
              onChange={transferMethod => updateDrawerState({ transferMethod })}
              data-testid="new-transfer-form-method-selector"
              boxProps={{ w: '100%' }}
              size={FormControlSizes.Default}
            />
          </FormGroup>
        )}

        {/* Initiate Transfer specific fields */}
        {drawerState.transferMethod === 'initiate' &&
          drawerState.transferType === CustomerLedgerUpdateTypeEnum.Withdraw && (
            <>
              {/* Provider */}
              <FormGroup mb="spacing16" label="Provider">
                <SearchSelect
                  selection={providerOptions.find(p => p.Name === drawerState.provider)}
                  options={providerOptions}
                  onChange={selectedProvider =>
                    updateDrawerState({
                      provider: selectedProvider?.Name || '',
                      source: undefined,
                      destination: undefined,
                    })
                  }
                  getLabel={selectedProvider => selectedProvider.DisplayName}
                  data-testid="new-transfer-form-provider-select"
                  placeholder="Select Provider"
                  showClear
                />
              </FormGroup>

              {/* Asset and Amount Row - Read-only display */}
              <HStack gap="spacing16" mb="spacing16">
                <Box flex={1}>
                  <FormGroup label="Asset">
                    <AssetAliasSelector
                      selectedCurrency={drawerState.currency}
                      selectedAlias={drawerState.aliasCurrency}
                      onChange={alias => updateDrawerState({ aliasCurrency: alias })}
                      disabled={!drawerState.currency}
                    />
                  </FormGroup>
                </Box>
                <Box flex={1}>
                  <FormGroup label="Amount">
                    <NumberInput
                      value={drawerState.amount ?? ''}
                      suffix={drawerState.aliasCurrency}
                      onChange={noop} // Read-only field, no action needed
                      readOnly={true}
                      data-testid="new-transfer-form-amount-input-disabled"
                    />
                  </FormGroup>
                </Box>
              </HStack>

              {/* Source */}
              <FormGroup mb="spacing16" label="Source">
                <SearchSelect
                  selection={sourceOptions.find(s => s.marketAccount.Name === drawerState.source)}
                  options={sourceOptions}
                  onChange={selectedSource => updateDrawerState({ source: selectedSource?.marketAccount.Name })}
                  getLabel={selectedSource => selectedSource?.label || ''}
                  data-testid="new-transfer-form-source-select"
                  placeholder="Select Source"
                  disabled={!drawerState.provider}
                  showClear
                />
                {drawerState.source && (
                  <Text color="colorTextSubtle" fontSize="fontSizeSmall" mt="spacing2">
                    Available Balance:{' '}
                    <InlineFormattedNumber
                      number={sourceAccountBalance?.AvailableAmount ?? 0}
                      currency={sourceAccountBalance?.Currency ?? drawerState.aliasCurrency}
                    />
                  </Text>
                )}
              </FormGroup>

              {/* Destination */}
              <FormGroup mb="spacing16" label="Destination">
                <SearchSelect
                  selection={destinationOptions.find(d => d.marketAccount.Name === drawerState.destination)}
                  options={destinationOptions}
                  onChange={selectedDestination =>
                    updateDrawerState({ destination: selectedDestination?.marketAccount.Name || '' })
                  }
                  getLabel={selectedDestination => selectedDestination?.label || ''}
                  data-testid="new-transfer-form-destination-select"
                  placeholder="Select Destination"
                  disabled={!drawerState.provider}
                  showClear
                />
              </FormGroup>
            </>
          )}

        {/* Manual Transfer specific fields */}
        {drawerState.transferMethod === 'manual' && (
          <>
            {/* TX Hash Field */}
            <FormGroup
              mb="spacing16"
              label="TX Hash"
              tooltip="Optional transaction hash for manually created transactions. Recommended for record keeping and reconciliation."
            >
              <Input
                value={drawerState.txHash}
                autoComplete="off"
                onChange={e => updateDrawerState({ txHash: e.target.value })}
                data-testid="new-transfer-form-tx-hash-input"
                placeholder="Optional"
              />
            </FormGroup>
          </>
        )}

        {!(showOnlyConfirmTransferFlow && drawerState.transferMethod === 'manual') && (
          <>
            {/* External ID */}
            <FormGroup mb="spacing16" label="External ID*">
              <Input
                value={drawerState.externalID}
                autoComplete="off"
                onChange={e => updateDrawerState({ externalID: e.target.value })}
                data-testid="new-transfer-form-external-id-input"
                placeholder="Required"
                maxLength={36}
                suffix={
                  <Button
                    size={FormControlSizes.Small}
                    variant={ButtonVariants.Default}
                    onClick={handleGenerateExternalID}
                    ghost
                  >
                    Generate
                  </Button>
                }
              />
            </FormGroup>

            <Divider mt="spacing16" mb="spacing16" />

            {/* Internal Comments */}
            <FormGroup mb="spacing16" label="Internal Comment">
              <Input
                value={drawerState.internalComments}
                autoComplete="off"
                onChange={e => updateDrawerState({ internalComments: e.target.value })}
                placeholder="Optional"
                data-testid="new-transfer-form-internal-comments"
              />
            </FormGroup>

            {/* External Comments */}
            <FormGroup mb="spacing16" label="External Comment">
              <Input
                value={drawerState.externalComments}
                autoComplete="off"
                onChange={e => updateDrawerState({ externalComments: e.target.value })}
                placeholder="Optional"
                data-testid="new-transfer-form-external-comments"
              />
            </FormGroup>
          </>
        )}
      </Box>
    </VStack>
  );
}
